<template>
  <div class="ap-card p-0 overflow-hidden otp-section-container">
    <div class="card-header-styled">
      <i class="fas fa-shield-alt me-1"></i>Security Verification
    </div>
    <div class="p-4 text-center">
      <div class="otp-icon-wrap mb-3">
        <i class="fas fa-user-check text-primary display-4"></i>
      </div>
      <h4 class="fw-bold mb-2">Confirm Your Identity</h4>
      <p class="text-muted mb-4 px-lg-5">
        We've sent a 6-digit verification code to <br />
        <strong class="text-dark">{{ mobile }}</strong> and
        <strong class="text-dark">{{ email }}</strong
        >.
      </p>

      <div class="otp-input-container mb-4">
        <div class="d-flex justify-content-center gap-2">
          <input
            v-for="(_, idx) in 6"
            :key="idx"
            :value="otpDigits[idx]"
            @input="onInput($event, idx)"
            @keydown.backspace="onBackspace($event, idx)"
            type="text"
            maxlength="1"
            class="form-control otp-digit-inp"
            :ref="(el) => (inputs[idx] = el)"
          />
        </div>
        <span v-if="errors.otp" class="err-msg mt-3 d-block">{{ errors.otp }}</span>
      </div>

      <div class="d-flex flex-column gap-3 align-items-center">
        <button type="button" class="btn-book w-100 max-w-300" @click="$emit('verify')" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="fas fa-check-circle me-2"></i>Verify & Confirm Appointment
        </button>

        <div class="otp-timer text-muted small">
          Didn't receive code?
          <button type="button" class="btn-link-styled" :disabled="resendTimer > 0" @click="$emit('resend')">
            {{ resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code' }}
          </button>
        </div>

        <button type="button" class="btn btn-link text-muted btn-sm mt-2" @click="$emit('back')">
          <i class="fas fa-arrow-left me-1"></i>Back to details
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
  mobile: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    required: true,
  },
  otpDigits: {
    type: Array,
    required: true,
  },
  resendTimer: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:otpDigits', 'verify', 'resend', 'back']);

const inputs = ref([]);

const onInput = (event, idx) => {
  const val = event.target.value;
  const newOtp = [...props.otpDigits];
  newOtp[idx] = val;
  emit('update:otpDigits', newOtp);

  if (val && idx < 5 && inputs.value[idx + 1]) {
    inputs.value[idx + 1].focus();
  }
};

const onBackspace = (event, idx) => {
  if (event.key === 'Backspace' && !props.otpDigits[idx] && idx > 0 && inputs.value[idx - 1]) {
    const newOtp = [...props.otpDigits];
    newOtp[idx - 1] = '';
    emit('update:otpDigits', newOtp);
    inputs.value[idx - 1].focus();
  }
};

onMounted(() => {
  if (inputs.value[0]) inputs.value[0].focus();
});
</script>

<style scoped>
.ap-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 3px 16px rgba(115, 103, 240, 0.1);
}

.card-header-styled {
  background: #f8f8f8;
  padding: 12px 20px;
  border-bottom: 1px solid #e4e1f0;
  font-size: 1rem;
  font-weight: 700;
  color: #2d2d3a;
  display: flex;
  align-items: center;
}
.card-header-styled i {
  color: #7367f0;
  font-size: 1.1rem;
}

.otp-digit-inp {
  width: 45px;
  height: 55px;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  border-radius: 10px;
  border: 2px solid #e4e1f0;
  transition: all 0.2s;
}
.otp-digit-inp:focus {
  border-color: #7367f0;
  box-shadow: 0 0 0 4px #ede9fd;
}

.max-w-300 {
  max-width: 300px;
}
.btn-link-styled {
  background: none;
  border: none;
  color: #7367f0;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
}
.btn-link-styled:disabled {
  color: #6e6b7b;
  cursor: not-allowed;
}

.err-msg {
  display: block;
  font-size: 0.75rem;
  color: #ea5455;
  margin-top: 4px;
  font-weight: 500;
}

.btn-book {
  background: linear-gradient(135deg, #7367f0 0%, #9186f3 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 10px 36px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(115, 103, 240, 0.4);
}
.btn-book:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(115, 103, 240, 0.45);
}
.btn-book:active {
  transform: translateY(0);
}
</style>
